import { getOverlayFamily } from '../assets/spriteIndex.mjs';

/**
 * Motion profiles per overlay family.
 * Each profile defines parameters for pure-function animation.
 *   bob:   { amp, freq }         vertical oscillation (pixels, Hz)
 *   sway:  { amp, freq }         rotation oscillation (degrees, Hz)
 *   pulse: { amp, freq }         scale oscillation (fraction, Hz)
 *   rare:  { scaleAmp, chance }  infrequent scale kick (fraction, period seed range)
 */
const PROFILES = {
  animal_land:   { bob: { amp: 1.5, freq: 0.6 },  sway: null,                      pulse: { amp: 0.012, freq: 0.8 }, rare: { scaleAmp: 0.03, minPeriod: 20, maxPeriod: 50 } },
  animal_water:  { bob: { amp: 2.0, freq: 0.45 },  sway: { amp: 1.0, freq: 0.3 },  pulse: null,                       rare: null },
  forest:        { bob: null,                        sway: { amp: 1.5, freq: 0.25 }, pulse: null,                       rare: null },
  vegetation:    { bob: { amp: 0.8, freq: 0.35 },   sway: { amp: 1.2, freq: 0.3 },  pulse: null,                       rare: null },
  ship:          { bob: { amp: 2.5, freq: 0.3 },    sway: { amp: 1.0, freq: 0.2 },  pulse: null,                       rare: null },
  dragon:        { bob: { amp: 2.0, freq: 0.4 },    sway: null,                      pulse: { amp: 0.015, freq: 0.5 }, rare: { scaleAmp: 0.04, minPeriod: 15, maxPeriod: 45 } },
  structure:     { bob: null,                        sway: null,                      pulse: { amp: 0.005, freq: 0.15 },rare: null },
  castle_ruin:   { bob: null,                        sway: null,                      pulse: { amp: 0.005, freq: 0.12 },rare: null },
  flying_castle: { bob: { amp: 2.0, freq: 0.25 },   sway: { amp: 0.6, freq: 0.15 }, pulse: null,                       rare: null },
  mountainrocks: { bob: null,                        sway: null,                      pulse: { amp: 0.003, freq: 0.1 }, rare: null },
  island_water:  { bob: { amp: 1.0, freq: 0.35 },   sway: { amp: 0.5, freq: 0.2 },  pulse: null,                       rare: null },
  island_land:   { bob: null,                        sway: { amp: 0.4, freq: 0.15 }, pulse: null,                       rare: null },
  campsite:      { bob: null,                        sway: null,                      pulse: { amp: 0.008, freq: 0.2 }, rare: null },
  geysers:       { bob: { amp: 1.0, freq: 0.5 },    sway: null,                      pulse: { amp: 0.01, freq: 0.6 },  rare: { scaleAmp: 0.03, minPeriod: 10, maxPeriod: 30 } },
  graveyard:     { bob: null,                        sway: { amp: 0.5, freq: 0.1 },  pulse: null,                       rare: null },
};

const DEG2RAD = Math.PI / 180;

/**
 * Simple deterministic hash for seed generation.
 * Returns a float 0..1.
 */
function hash(q, r, worldSeed) {
  let h = (q * 374761393 + r * 668265263 + worldSeed * 2147483647) | 0;
  h = ((h ^ (h >> 13)) * 1274126177) | 0;
  h = (h ^ (h >> 16)) | 0;
  return (h & 0x7fffffff) / 0x7fffffff;
}

/**
 * Compute overlay transform for a given tile, purely from nowMs + seed.
 * Returns { tx, ty, rotation, scaleX, scaleY } (all small deltas).
 *
 * @param {string} overlayKey
 * @param {number} q
 * @param {number} r
 * @param {number} worldSeed
 * @param {number} nowMs
 * @returns {{ tx: number, ty: number, rotation: number, scale: number }}
 */
export function computeOverlayMotion(overlayKey, q, r, worldSeed, nowMs) {
  const family = getOverlayFamily(overlayKey);
  const profile = PROFILES[family] || PROFILES.vegetation;

  const seed = hash(q, r, worldSeed);
  const phaseOffset = seed * Math.PI * 2;
  const ampMul = 0.6 + seed * 0.6; // 0.6..1.2
  const t = nowMs / 1000; // seconds

  let tx = 0;
  let ty = 0;
  let rotation = 0;
  let scale = 1;

  // Bob (vertical)
  if (profile.bob) {
    ty = Math.sin(t * profile.bob.freq * Math.PI * 2 + phaseOffset) * profile.bob.amp * ampMul;
  }

  // Sway (rotation)
  if (profile.sway) {
    rotation = Math.sin(t * profile.sway.freq * Math.PI * 2 + phaseOffset + 1.3) * profile.sway.amp * ampMul * DEG2RAD;
  }

  // Pulse (scale)
  if (profile.pulse) {
    scale += Math.sin(t * profile.pulse.freq * Math.PI * 2 + phaseOffset + 2.7) * profile.pulse.amp * ampMul;
  }

  // Rare event (extra scale kick)
  if (profile.rare) {
    const period = profile.rare.minPeriod + seed * (profile.rare.maxPeriod - profile.rare.minPeriod);
    const cyclePos = ((t + seed * period) % period) / period;
    // Kick happens in first 5% of cycle
    if (cyclePos < 0.05) {
      const kickT = cyclePos / 0.05; // 0..1
      const kick = Math.sin(kickT * Math.PI) * profile.rare.scaleAmp;
      scale += kick;
    }
  }

  return { tx, ty, rotation, scale };
}

// ---- Hover sparkle system ----

/**
 * Generate sparkle positions for a hovered overlay tile.
 * Returns array of { x, y, alpha, size } in screen-pixel offsets from tile centre.
 * Pure function of nowMs + seed.
 *
 * @param {number} q
 * @param {number} r
 * @param {number} worldSeed
 * @param {number} nowMs
 * @param {number} hexScreenSize - sz (hex radius in screen pixels)
 * @returns {Array<{ x: number, y: number, alpha: number, size: number }>}
 */
export function computeHoverSparkles(q, r, worldSeed, nowMs, hexScreenSize) {
  const sparkles = [];
  const seed = hash(q, r, worldSeed);
  // Emit a batch every 800ms
  const batchInterval = 800;
  const batchIndex = Math.floor(nowMs / batchInterval);
  const batchT = (nowMs % batchInterval) / batchInterval; // 0..1 within batch

  const count = 3 + Math.floor(seed * 3); // 3..5
  const radius = hexScreenSize * 0.6;

  for (let i = 0; i < count; i++) {
    const s = hash(batchIndex, i, worldSeed + q * 31 + r * 17);
    const angle = s * Math.PI * 2;
    const dist = (0.3 + s * 0.7) * radius;
    // Sparkle drifts outward and fades
    const drift = 1 + batchT * 0.3;
    const alpha = (1 - batchT) * 0.6;
    if (alpha < 0.02) continue;
    sparkles.push({
      x: Math.cos(angle) * dist * drift,
      y: Math.sin(angle) * dist * drift,
      alpha,
      size: 1.5 + s * 1.5,
    });
  }
  return sparkles;
}
